import { GridSelectionModel } from '@mui/x-data-grid';
import { useState, useCallback, useEffect, useMemo } from 'react';
import _ from 'lodash';
import { defaultPageSize } from 'constants/datagrid';
import { GenericResource, ListResponse, QueryParams } from 'types/api';
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  QueryDefinition,
  skipToken,
} from '@reduxjs/toolkit/dist/query/react';
import { UseQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';

export interface useServerSideDataGridProps<
  BaseResource extends GenericResource,
  ModifiedResource extends GenericResource = BaseResource
> {
  useResourceHook: UseQuery<
    QueryDefinition<
      QueryParams<BaseResource> & { overrideOrgId?: string },
      BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
      any,
      ListResponse<BaseResource>
    >
  >;
  usePrefetch: any;
  prefetchName: string;
  params: Omit<QueryParams<BaseResource>, 'schema'> & {
    schema?: QueryParams<ModifiedResource>['schema'];
    overrideOrgId?: string;
  };
  disableRowSelection?: boolean;
  skip?: boolean;
  distinct?: boolean;
}

const useServerSideDataGrid = <
  BaseResource extends GenericResource,
  ModifiedResource extends GenericResource = BaseResource
>({
  useResourceHook,
  usePrefetch,
  prefetchName,
  params,
  disableRowSelection, // NOTE: this will be removed when all datagrids are updated to use rowActions
  skip = false,
  distinct = false,
}: useServerSideDataGridProps<BaseResource, ModifiedResource>) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [filterModel, setFilterModel] = useState<
    QueryParams<BaseResource>['filters']
  >([]);
  const [excludeModel, setExcludeModel] = useState<
    QueryParams<BaseResource>['excludes']
  >([]);
  const [sortModel, setSortModel] = useState<QueryParams<BaseResource>['sort']>(
    []
  );
  const [quickFilterValues, setQuickFilterValues] = useState<string[]>([]);
  const [selectedRows, setSelectedRows] = useState<GridSelectionModel>([]);
  const [searchSchema, setSearchSchema] = useState<
    QueryParams<BaseResource>['searchSchema']
  >(params.searchSchema || []);

  const [memoizedParams, setMemoizedParams] = useState<typeof params>(params);
  useEffect(() => {
    if (!_.isEqual(memoizedParams, params)) {
      setMemoizedParams(params);
    }
  }, [memoizedParams, params]);

  const handleSetFilterModel = useCallback(
    (newFilterModel: QueryParams<BaseResource>['filters']) => {
      if (!_.isEqual(filterModel, newFilterModel)) {
        setFilterModel(newFilterModel);
      }
    },
    [filterModel, setFilterModel]
  );

  const handleSetExcludeModel = useCallback(
    (newExcludeModel: QueryParams<BaseResource>['excludes']) => {
      if (!_.isEqual(excludeModel, newExcludeModel)) {
        setExcludeModel(newExcludeModel);
      }
    },
    [excludeModel, setExcludeModel]
  );

  const handleSetSearchSchema = useCallback(
    (newSearchSchema: QueryParams<BaseResource>['searchSchema']) => {
      if (!_.isEqual(searchSchema, newSearchSchema)) {
        setSearchSchema(newSearchSchema);
      }
    },
    [searchSchema, setSearchSchema]
  );

  const queryParams = useMemo(
    () =>
      ({
        pageSize,
        pageNumber: page,
        sort: [
          ...(sortModel || []),
          ...(memoizedParams.sort ? memoizedParams.sort : []),
        ],
        filters: [...(filterModel || []), ...(memoizedParams.filters || [])],
        excludes: [...(excludeModel || []), ...(memoizedParams.excludes || [])],
        schema: memoizedParams.schema,
        noCache: memoizedParams.noCache,
        search: params.search
          ? params.search
          : quickFilterValues.length
          ? quickFilterValues.join(' ').replace(/,/g, '')
          : '',
        searchSchema,
        newVersion: memoizedParams.newVersion,
        blended: memoizedParams.blended,
        distinct,
        overrideOrgId: params.overrideOrgId,
      } as QueryParams<BaseResource> & { overrideOrgId?: string }),
    [
      filterModel,
      excludeModel,
      page,
      pageSize,
      memoizedParams,
      quickFilterValues,
      sortModel,
      searchSchema,
      params.search,
      params.overrideOrgId,
      distinct,
    ]
  );

  const resource = useResourceHook(
    skip
      ? skipToken
      : (queryParams as QueryParams<BaseResource> & { overrideOrgId?: string })
  );

  const prefetch = usePrefetch(prefetchName);

  const prefetchNext = useCallback(() => {
    prefetch({
      ...queryParams,
      pageNumber: page + 1,
    });
  }, [prefetch, page, queryParams]);

  useEffect(() => {
    if (!skip) {
      prefetchNext();
    }
  }, [page, skip, prefetchNext]);

  if (disableRowSelection) {
    return {
      ...resource,
      data: resource.data as ListResponse<ModifiedResource> | undefined,
      page,
      pageSize,
      filterModel,
      excludeModel,
      sortModel,
      queryParams,
      selectedRows,
      setters: {
        setPage,
        setPageSize,
        setFilterModel: handleSetFilterModel,
        setExcludeModel: handleSetExcludeModel,
        setQuickFilterValues,
        setSortModel,
        setSearchSchema: handleSetSearchSchema,
        onSelectionModelChange: undefined,
      },
    };
  }

  return {
    ...resource,
    data: resource.data as ListResponse<ModifiedResource> | undefined,
    page,
    pageSize,
    filterModel,
    excludeModel,
    sortModel,
    // NOTE: this will be removed when all datagrids are updated to use rowActions
    selectedRows,
    queryParams,
    setters: {
      setPage,
      setPageSize,
      setFilterModel: handleSetFilterModel,
      setExcludeModel: handleSetExcludeModel,
      setQuickFilterValues,
      setSortModel,
      setSearchSchema: handleSetSearchSchema,
      // NOTE: this will be removed when all datagrids are updated to use rowActions
      onSelectionModelChange: setSelectedRows,
    },
  };
};

export default useServerSideDataGrid;
